import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  CardFooter,
  Button, 
  OutlineButton, 
  Badge, 
  FlexContainer,
  Input,
  Select,
  FormGroup,
  Label,
  TextArea,
  Grid
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import { EmployeeLoan } from '../../types';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const Summary = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SummaryCard = styled.div<{ type?: string }>`
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  padding: 1.5rem;
  text-align: center;
  
  h3 {
    margin: 0 0 0.5rem 0;
    color: var(--secondary-color);
    font-size: 1rem;
  }
  
  .amount {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${props => props.type === 'balance' ? 'var(--danger-color)' : 
      props.type === 'installment' ? 'var(--primary-color)' : 'var(--text-color)'};
  }
  
  .label {
    font-size: 0.85rem;
    color: var(--dark-gray);
    margin-top: 0.5rem;
  }
`;

const DetailSection = styled.div`
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border-color);
  
  &:last-child {
    border-bottom: none;
  }
  
  .label {
    font-weight: 500;
    color: var(--dark-gray);
  }
  
  .value {
    font-weight: 600;
    color: var(--text-color);
  }
`;

const AmountText = styled.span<{ type?: 'positive' | 'negative' | 'neutral' }>`
  font-weight: 500;
  color: ${props => props.type === 'positive' ? 'var(--success-color)' : 
    props.type === 'negative' ? 'var(--danger-color)' : 'var(--text-color)'};
`;

const DateText = styled.span`
  color: var(--dark-gray);
  font-size: 0.85rem;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--dark-gray);
`;

const LoanPayment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loan, setLoan] = useState<EmployeeLoan | null>(null);
  const [loading, setLoading] = useState(true);
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<string>('Bank Transfer');
  const [paymentNotes, setPaymentNotes] = useState<string>('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const loadLoan = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const response = await apiService.getEmployeeLoan(id);
        if (response.success && response.data) {
          setLoan(response.data);
        } else {
          setLoan(null);
        }
      } catch (error) {
        console.error('Error loading loan:', error);
        setLoan(null);
      } finally {
        setLoading(false);
      }
    };

    loadLoan();
  }, [id]);

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'TZS',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusVariant = (status: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' | 'secondary' => {
    switch (status) {
      case 'Active': return 'info';
      case 'Completed': return 'success';
      case 'Defaulted': return 'danger';
      case 'Cancelled': return 'warning';
      default: return 'info';
    }
  };

  const handlePayment = async () => {
    if (!loan || paymentAmount <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    if (paymentAmount > loan.balance) {
      alert('Payment amount cannot exceed the remaining balance');
      return;
    }

    setProcessing(true);
    
    try {
      // Find the next unpaid installment
      const unpaidInstallment = loan.installments.find(inst => inst.status === 'Pending');
      if (!unpaidInstallment) {
        alert('No unpaid installments found');
        setProcessing(false);
        return;
      }

      const response = await apiService.processLoanPayment(loan.id, {
        installmentNumber: unpaidInstallment.installmentNumber,
        paymentReference: `PAY-${Date.now()}`,
        notes: paymentNotes
      });

      if (response.success) {
        alert('Payment processed successfully!');
        navigate(`/office/loans/${loan.id}`);
      } else {
        alert(response.message || 'Failed to process payment');
      }
    } catch (error: any) {
      console.error('Error processing payment:', error);
      alert('Failed to process payment');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return <div>Loading loan details...</div>;
  }

  if (!loan) {
    return (
      <div>
        <PageHeader>
          <h1 className="page-title">Loan Not Found</h1>
          <OutlineButton as={Link} to="/office/loans">
            Back to Loans
          </OutlineButton>
        </PageHeader>
        <Card>
          <CardBody>
            <NoDataMessage>
              <p>The requested loan could not be found.</p>
            </NoDataMessage>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (loan.status !== 'Active' && loan.status !== 'Approved') {
    return (
      <div>
        <PageHeader>
          <h1 className="page-title">Payment Not Allowed</h1>
          <OutlineButton as={Link} to="/office/loans">
            Back to Loans
          </OutlineButton>
        </PageHeader>
        <Card>
          <CardBody>
            <NoDataMessage>
              <p>Payments are only allowed for active or approved loans. This loan has status: {loan.status}</p>
            </NoDataMessage>
          </CardBody>
        </Card>
      </div>
    );
  }

  const remainingBalance = loan.balance;
  const monthlyInstallment = loan.installmentAmount;

  return (
    <div>
      <PageHeader>
        <div>
          <h1 className="page-title">Make Loan Payment</h1>
          <p style={{ color: 'var(--dark-gray)', margin: '0.5rem 0 0 0' }}>
            {loan.id} - {loan.purpose}
          </p>
        </div>
        <FlexContainer gap="0.5rem">
          <OutlineButton as={Link} to={`/office/loans/${loan.id}`}>
            Back to Loan Details
          </OutlineButton>
          <OutlineButton as={Link} to="/office/loans">
            Back to Loans
          </OutlineButton>
        </FlexContainer>
      </PageHeader>

      <Summary>
        <SummaryCard type="balance">
          <h3>Remaining Balance</h3>
          <div className="amount">{formatAmount(remainingBalance)}</div>
          <div className="label">Amount still owed</div>
        </SummaryCard>
        
        <SummaryCard type="installment">
          <h3>Monthly Installment</h3>
          <div className="amount">{formatAmount(monthlyInstallment)}</div>
          <div className="label">Regular payment amount</div>
        </SummaryCard>
        
        <SummaryCard>
          <h3>Interest Rate</h3>
          <div className="amount">{loan.interestRate}%</div>
          <div className="label">Annual interest rate</div>
        </SummaryCard>
        
        <SummaryCard>
          <h3>Loan Status</h3>
          <div className="amount">
            <Badge variant={getStatusVariant(loan.status)}>
              {loan.status}
            </Badge>
          </div>
          <div className="label">Current status</div>
        </SummaryCard>
      </Summary>

      <Grid columns="1fr 1fr" gap="1.5rem">
        <DetailSection>
          <h3 style={{ marginBottom: '1rem', color: 'var(--secondary-color)' }}>
            Loan Information
          </h3>
          <DetailRow>
            <span className="label">Loan ID:</span>
            <span className="value">{loan.id}</span>
          </DetailRow>
          <DetailRow>
            <span className="label">Purpose:</span>
            <span className="value">{loan.purpose}</span>
          </DetailRow>
          <DetailRow>
            <span className="label">Employee:</span>
            <span className="value">{loan.employeeName || 'N/A'}</span>
          </DetailRow>
          <DetailRow>
            <span className="label">Principal Amount:</span>
            <AmountText type="neutral">{formatAmount(loan.principal)}</AmountText>
          </DetailRow>
          <DetailRow>
            <span className="label">Interest Rate:</span>
            <span className="value">{loan.interestRate}%</span>
          </DetailRow>
          <DetailRow>
            <span className="label">Duration:</span>
            <span className="value">{loan.duration} months</span>
          </DetailRow>
          <DetailRow>
            <span className="label">Start Date:</span>
            <DateText>{formatDate(loan.startDate)}</DateText>
          </DetailRow>
          <DetailRow>
            <span className="label">End Date:</span>
            <DateText>{formatDate(loan.endDate)}</DateText>
          </DetailRow>
        </DetailSection>

        <DetailSection>
          <h3 style={{ marginBottom: '1rem', color: 'var(--secondary-color)' }}>
            Payment Details
          </h3>
          <FormGroup>
            <Label htmlFor="paymentAmount">Payment Amount (TZS)</Label>
            <Input
              id="paymentAmount"
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(Number(e.target.value))}
              min="0"
              max={remainingBalance}
              step="1000"
            />
            <small style={{ color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
              Maximum payment: {formatAmount(remainingBalance)}
            </small>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="paymentMethod">Payment Method</Label>
            <Select
              id="paymentMethod"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="Cash">Cash</option>
              <option value="Check">Check</option>
              <option value="Mobile Money">Mobile Money</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="paymentNotes">Payment Notes (Optional)</Label>
            <TextArea
              id="paymentNotes"
              value={paymentNotes}
              onChange={(e) => setPaymentNotes(e.target.value)}
              placeholder="Enter any additional notes about this payment..."
              rows={3}
            />
          </FormGroup>

          <div style={{ 
            background: 'var(--light-gray)', 
            padding: '1rem', 
            borderRadius: 'var(--border-radius)',
            marginTop: '1rem'
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--secondary-color)' }}>
              Payment Summary
            </h4>
            <DetailRow>
              <span className="label">Payment Amount:</span>
              <AmountText type="negative">{formatAmount(paymentAmount)}</AmountText>
            </DetailRow>
            <DetailRow>
              <span className="label">Remaining After Payment:</span>
              <AmountText type={remainingBalance - paymentAmount > 0 ? 'negative' : 'positive'}>
                {formatAmount(remainingBalance - paymentAmount)}
              </AmountText>
            </DetailRow>
            <DetailRow>
              <span className="label">Payment Method:</span>
              <span className="value">{paymentMethod}</span>
            </DetailRow>
            <DetailRow>
              <span className="label">Processed By:</span>
              <span className="value">{user?.name}</span>
            </DetailRow>
          </div>
        </DetailSection>
      </Grid>

      <Card style={{ marginTop: '1.5rem' }}>
        <CardHeader>
          <h3>Recent Payment History</h3>
        </CardHeader>
        <CardBody>
          {loan.installments.filter(i => i.status === 'Paid').length > 0 ? (
            <div>
              <p style={{ marginBottom: '1rem', color: 'var(--dark-gray)' }}>
                Showing last 5 payments
              </p>
              {loan.installments
                .filter(i => i.status === 'Paid')
                .slice(-5)
                .map((installment) => (
                  <div key={installment.id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.75rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--border-radius)',
                    marginBottom: '0.5rem',
                    background: 'var(--light-gray)'
                  }}>
                    <div>
                      <div style={{ fontWeight: '500' }}>
                        Installment #{installment.installmentNumber}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--dark-gray)' }}>
                        {formatDate(installment.dueDate)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: '500' }}>
                        {formatAmount(installment.amount)}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--dark-gray)' }}>
                        {installment.paidDate ? formatDate(installment.paidDate) : 'N/A'}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <NoDataMessage>
              <p>No payment history available for this loan.</p>
            </NoDataMessage>
          )}
        </CardBody>
      </Card>

      <CardFooter style={{ marginTop: '1.5rem' }}>
        <FlexContainer gap="0.5rem" justify="flex-end">
          <OutlineButton as={Link} to={`/office/loans/${loan.id}`}>
            Cancel
          </OutlineButton>
          <Button 
            onClick={handlePayment}
            disabled={processing || paymentAmount <= 0 || paymentAmount > remainingBalance}
          >
            {processing ? 'Processing Payment...' : 'Process Payment'}
          </Button>
        </FlexContainer>
      </CardFooter>

      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
    </div>
  );
};

export default LoanPayment; 